import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscriptionsAPI } from '../../api/subscriptions';
import { formatCurrency, formatDateTime, formatPhone } from '../../utils/formatters';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import PurchaseSubscriptionForm from './PurchaseSubscriptionForm';
import './SubscriptionManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',       label: 'All',           icon: '📋', status: null },
  { id: 'active',    label: 'Active',        icon: '✅', status: 'active' },
  { id: 'expiring',  label: 'Expiring Soon', icon: '⏰', status: 'expiring' },
  { id: 'depleted',  label: 'Depleted',      icon: '🪙', status: 'depleted' },
  { id: 'expired',   label: 'Expired',       icon: '⏸️', status: 'expired' },
  { id: 'cancelled', label: 'Cancelled',     icon: '❌', status: 'cancelled' },
];

const STATUS_META = {
  active:    { label: 'Active',    bg: '#d1fae5', color: '#065f46' },
  expiring:  { label: 'Expiring',  bg: '#fef3c7', color: '#92400e' },
  depleted:  { label: 'Depleted',  bg: '#e0e7ff', color: '#3730a3' },
  expired:   { label: 'Expired',   bg: '#fee2e2', color: '#991b1b' },
  cancelled: { label: 'Cancelled', bg: '#f3f4f6', color: '#6b7280' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getDaysRemaining = (endDate) => {
  if (!endDate) return null;
  const diffMs = new Date(endDate) - new Date();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

const usagePercent = (sub) => {
  if (sub.planType === 'credit') {
    return sub.creditIssued > 0
      ? Math.round((sub.creditRemaining / sub.creditIssued) * 100)
      : 0;
  }
  if (sub.planType === 'pieces') {
    return sub.piecesIssued > 0
      ? Math.round((sub.piecesRemaining / sub.piecesIssued) * 100)
      : 0;
  }
  return null;
};

const renderBalance = (sub) => {
  if (sub.planType === 'credit') {
    return (
      <div>
        <strong style={{ color: '#0f172a' }}>{formatCurrency(sub.creditRemaining ?? 0)}</strong>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          of {formatCurrency(sub.creditIssued ?? 0)}
        </div>
      </div>
    );
  }
  if (sub.planType === 'pieces') {
    return (
      <div>
        <strong style={{ color: '#0f172a' }}>{sub.piecesRemaining ?? 0} pcs</strong>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          of {sub.piecesIssued ?? 0}
        </div>
      </div>
    );
  }
  return <span style={{ color: '#94a3b8' }}>—</span>;
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, bg: '#f3f4f6', color: '#374151' };
  return <span className="om-badge" style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>;
};

const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const { showNotification } = useContext(NotificationContext);

  const [activeTab, setActiveTab]         = useState('all');
  const [subscriptions, setSubscriptions] = useState([]);
  const [totalCount, setTotalCount]       = useState(0);
  const [page, setPage]                   = useState(1);
  const [pages, setPages]                 = useState(1);
  const [loading, setLoading]             = useState(false);

  const [search, setSearch]         = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd]   = useState('');

  const [summary, setSummary] = useState(null);
  const [plans, setPlans]     = useState([]);

  const [selectedSub, setSelectedSub]     = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseLoading, setPurchaseLoading]     = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget]       = useState(null);
  const [cancelReason, setCancelReason]       = useState('');
  const [cancelLoading, setCancelLoading]     = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const res = await subscriptionsAPI.getDashboardSummary();
      setSummary(res.data);
    } catch { /* non-critical */ }
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const res = await subscriptionsAPI.getPlans({ isActive: true });
      setPlans(res.data);
    } catch { /* non-critical */ }
  }, []);

  const loadSubscriptions = useCallback(async (resetPage = false) => {
    setLoading(true);
    const p = resetPage ? 1 : page;
    if (resetPage) setPage(1);
    try {
      const params = {
        page: p,
        limit: 20,
        search: search || undefined,
        planId: filterPlan || undefined,
        startDate: filterStart || undefined,
        endDate: filterEnd || undefined,
      };
      const tabStatus = TABS.find(t => t.id === activeTab)?.status;
      if (tabStatus) {
        if (tabStatus === 'expiring') params.expiringSoon = 'true';
        else params.status = tabStatus;
      }

      const res = await subscriptionsAPI.getAll(params);
      setSubscriptions(res.data);
      setTotalCount(res.pagination.total);
      setPages(res.pagination.pages);
    } catch {
      showNotification('Failed to load subscriptions', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, filterPlan, filterStart, filterEnd]);

  useEffect(() => { loadSummary(); loadPlans(); }, []);
  useEffect(() => { loadSubscriptions(true); }, [activeTab, search, filterPlan, filterStart, filterEnd]);
  useEffect(() => { if (page > 0) loadSubscriptions(); }, [page]);

  const openDetail = async (sub) => {
    setSelectedSub(sub);
    setDetailLoading(true);
    try {
      const res = await subscriptionsAPI.getById(sub._id);
      setSelectedSub(res.data);
    } catch {
      showNotification('Failed to load subscription details', 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePurchase = async (data) => {
    setPurchaseLoading(true);
    try {
      await subscriptionsAPI.purchase(data);
      showNotification('Subscription sold successfully', 'success');
      setShowPurchaseModal(false);
      loadSubscriptions();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to sell subscription', 'error');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const openCancelModal = (sub, e) => {
    e?.stopPropagation();
    setCancelTarget(sub);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      showNotification('Please provide a cancellation reason', 'error');
      return;
    }
    setCancelLoading(true);
    try {
      await subscriptionsAPI.cancel(cancelTarget._id, { reason: cancelReason });
      showNotification('Subscription cancelled', 'success');
      setShowCancelModal(false);
      setCancelTarget(null);
      setCancelReason('');
      if (selectedSub?._id === cancelTarget._id) setSelectedSub(null);
      loadSubscriptions();
      loadSummary();
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to cancel subscription', 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  const tabCount = (statusKey) => {
    if (!summary || !statusKey) return null;
    if (statusKey === 'active') return summary.activeCount;
    if (statusKey === 'expiring') return summary.expiringSoonCount;
    if (statusKey === 'depleted') return summary.depletedCount;
    if (statusKey === 'expired') return summary.expiredCount;
    if (statusKey === 'cancelled') return summary.cancelledCount;
    return null;
  };

  const clearFilters = () => {
    setFilterPlan(''); setFilterStart(''); setFilterEnd('');
  };

  const hasFilters = filterPlan || filterStart || filterEnd;

  return (
    <div className="ps-root">

      <div className="ps-header">
        <div>
          <h1 className="ps-title">Customer Subscriptions</h1>
          <p className="ps-subtitle">Sell prepaid plans and track active customer balances</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" onClick={() => navigate('/subscriptions/plans')}>
            Manage Plans
          </Button>
          <Button variant="primary" onClick={() => setShowPurchaseModal(true)}>
            + Sell Subscription
          </Button>
        </div>
      </div>

      {summary && (
        <div className="ps-summary-row">
          <SummaryCard
            icon="📋" label="Total Subscriptions" accent="#3b82f6"
            value={summary.totalCount ?? 0}
            sub={`${summary.activeCount ?? 0} active`}
          />
          <SummaryCard
            icon="💰" label="Lifetime Revenue" accent="#10b981"
            value={formatCurrency(summary.totalRevenue ?? 0)}
            sub="prepaid sales"
          />
          <SummaryCard
            icon="🪙" label="Outstanding Credit" accent="#8b5cf6"
            value={formatCurrency(summary.outstandingCredit ?? 0)}
            sub="across active plans"
          />
          <SummaryCard
            icon="👕" label="Outstanding Pieces" accent="#06b6d4"
            value={summary.outstandingPieces ?? 0}
            sub="across active plans"
          />
          <SummaryCard
            icon="⏰" label="Expiring Soon" accent="#f59e0b"
            value={summary.expiringSoonCount ?? 0}
            sub="within 7 days"
          />
        </div>
      )}

      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setPage(1); }}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            {tab.status && tabCount(tab.status) != null && (
              <span className="om-tab-count">{tabCount(tab.status)}</span>
            )}
          </button>
        ))}
      </div>

      <div className="ps-content">
        <div className="ps-tab-pane">

          <div className="ps-filters">
            <div className="ps-search-wrap">
              <span className="ps-search-icon">🔍</span>
              <input
                className="ps-search-input"
                placeholder="Search by customer name or phone…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="om-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
            <select className="ps-filter-select" value={filterPlan} onChange={e => setFilterPlan(e.target.value)}>
              <option value="">All Plans</option>
              {plans.map(p => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </select>
            <input type="date" className="ps-filter-date" value={filterStart} onChange={e => setFilterStart(e.target.value)} />
            <span style={{ color: '#94a3b8', fontSize: 13 }}>→</span>
            <input type="date" className="ps-filter-date" value={filterEnd} onChange={e => setFilterEnd(e.target.value)} />
            {hasFilters && (
              <button className="om-clear-filters" onClick={clearFilters}>Clear filters ✕</button>
            )}
            <Button variant="secondary" onClick={() => { loadSubscriptions(); loadSummary(); }}>↺</Button>
          </div>

          <div className="ps-table-meta">
            <span className="ps-count">{totalCount} subscription{totalCount !== 1 ? 's' : ''}</span>
            {summary && activeTab === 'active' && (
              <span>
                Outstanding credit: <strong style={{ color: '#10b981' }}>{formatCurrency(summary.outstandingCredit ?? 0)}</strong>
              </span>
            )}
          </div>

          {loading ? (
            <div className="ps-loading">Loading subscriptions…</div>
          ) : (
            <>
              <div className="ps-table-wrap">
                <table className="ps-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Plan</th>
                      <th>Type</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Purchased</th>
                      <th>Expires</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="ps-empty">
                          <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                          No subscriptions found
                        </td>
                      </tr>
                    ) : subscriptions.map(sub => {
                      const daysLeft = getDaysRemaining(sub.endDate);
                      const isExpiringSoon = sub.status === 'active' && daysLeft != null && daysLeft <= 7;

                      return (
                        <tr key={sub._id} className="ps-row" onClick={() => openDetail(sub)}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="om-avatar-sm">
                                {sub.customer?.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="om-cust-name-cell">{sub.customer?.name}</div>
                                <div className="om-cust-phone-cell">{formatPhone(sub.customer?.phone)}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 700, color: '#6366f1', fontSize: 13 }}>
                              {sub.subscriptionPlan?.name}
                            </div>
                          </td>
                          <td>
                            <span className="om-badge" style={
                              sub.planType === 'pieces'
                                ? { background: '#ecfeff', color: '#155e75' }
                                : { background: '#eef2ff', color: '#3730a3' }
                            }>
                              {sub.planType === 'pieces' ? 'Pieces' : 'Credit'}
                            </span>
                          </td>
                          <td style={{ fontWeight: 700, color: '#0f172a' }}>
                            {formatCurrency(sub.pricePaid ?? 0)}
                          </td>
                          <td>{renderBalance(sub)}</td>
                          <td>
                            <StatusBadge status={sub.status} />
                            {isExpiringSoon && (
                              <div style={{ fontSize: 10, color: '#f59e0b', marginTop: 2, fontWeight: 600 }}>
                                ⏰ {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
                              </div>
                            )}
                          </td>
                          <td className="ps-date">{formatDateTime(sub.startDate).split(',')[0]}</td>
                          <td className="ps-date">
                            {sub.endDate
                              ? formatDateTime(sub.endDate).split(',')[0]
                              : <span style={{ color: '#94a3b8' }}>No expiry</span>}
                          </td>
                          <td onClick={e => e.stopPropagation()}>
                            {sub.status === 'active' && (
                              <button
                                className="om-action-btn danger"
                                onClick={e => openCancelModal(sub, e)}
                                title="Cancel subscription"
                              >
                                ❌
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {pages > 1 && (
                <div className="ps-pagination">
                  <Button variant="secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</Button>
                  <span className="ps-page-label">Page {page} of {pages}</span>
                  <Button variant="secondary" disabled={page === pages} onClick={() => setPage(p => p + 1)}>Next →</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail */}
      {selectedSub && (
        <Modal
          isOpen={!!selectedSub}
          onClose={() => setSelectedSub(null)}
          title={`Subscription — ${selectedSub.customer?.name}`}
          size="large"
        >
          {detailLoading ? (
            <div className="ps-loading">Loading details…</div>
          ) : (
            <SubscriptionDetailContent
              subscription={selectedSub}
              onCancel={openCancelModal}
            />
          )}
        </Modal>
      )}

      {/* Purchase */}
      <Modal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        title="Sell Subscription"
        size="medium"
      >
        <PurchaseSubscriptionForm
          plans={plans}
          onSubmit={handlePurchase}
          onCancel={() => setShowPurchaseModal(false)}
          loading={purchaseLoading}
        />
      </Modal>

      {/* Cancel */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => { setShowCancelModal(false); setCancelTarget(null); }}
        title="Cancel Subscription"
      >
        {cancelTarget && (
          <div className="ps-modal-form">
            <div className="om-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Cancel subscription for {cancelTarget.customer?.name}?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  Plan: <strong>{cancelTarget.subscriptionPlan?.name}</strong><br />
                  This deactivates the subscription immediately. Any remaining balance is forfeited.
                </div>
              </div>
            </div>
            <div className="ps-modal-field">
              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 6, display: 'block' }}>
                Cancellation reason *
              </label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                rows={3}
                style={{
                  width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 8,
                  padding: '9px 13px', fontSize: 14, fontFamily: "'DM Sans', sans-serif",
                  resize: 'vertical', lineHeight: 1.5,
                }}
                placeholder="Why is this subscription being cancelled?"
              />
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowCancelModal(false); setCancelTarget(null); }}>
                Keep Active
              </Button>
              <Button variant="danger" onClick={handleCancel} disabled={cancelLoading}>
                {cancelLoading ? 'Cancelling…' : 'Cancel Subscription'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Subscription Detail Content ──────────────────────────────────────────────
const SubscriptionDetailContent = ({ subscription, onCancel }) => {
  const daysLeft = getDaysRemaining(subscription.endDate);
  const plan = subscription.subscriptionPlan || {};
  const usagePct = usagePercent(subscription);

  return (
    <div className="sm-detail">

      <div className="om-detail-top-bar">
        <div className="om-detail-badges">
          <StatusBadge status={subscription.status} />
          {subscription.status === 'active' && daysLeft != null && daysLeft <= 7 && (
            <span className="om-badge" style={{ background: '#fef3c7', color: '#92400e' }}>
              ⏰ {daysLeft} day{daysLeft !== 1 ? 's' : ''} left
            </span>
          )}
          <span className="om-badge" style={
            subscription.planType === 'pieces'
              ? { background: '#ecfeff', color: '#155e75' }
              : { background: '#eef2ff', color: '#3730a3' }
          }>
            {subscription.planType === 'pieces' ? 'Pieces Plan' : 'Credit Plan'}
          </span>
        </div>
        {subscription.status === 'active' && (
          <button className="ps-settle-btn-lg" style={{ background: '#ef4444' }} onClick={(e) => onCancel(subscription, e)}>
            Cancel Subscription
          </button>
        )}
      </div>

      <div className="sm-detail-grid">

        {/* Customer */}
        <div className="om-detail-section">
          <div className="om-section-title">Customer</div>
          <div className="om-customer-card">
            <div className="om-avatar-lg">{subscription.customer?.name?.charAt(0).toUpperCase()}</div>
            <div className="om-customer-info">
              <div className="om-cust-name-lg">{subscription.customer?.name}</div>
              <div className="om-cust-detail">📞 {formatPhone(subscription.customer?.phone)}</div>
              {subscription.customer?.email && (
                <div className="om-cust-detail">✉️ {subscription.customer.email}</div>
              )}
            </div>
          </div>
        </div>

        {/* Plan */}
        <div className="om-detail-section">
          <div className="om-section-title">Plan</div>
          {[
            ['Plan',          plan.name],
            ['Type',          subscription.planType === 'pieces' ? 'Pieces' : 'Credit'],
            ['Price Paid',    formatCurrency(subscription.pricePaid ?? 0)],
            ['Payment',       subscription.paymentMethod?.toUpperCase()],
            ['Start',         formatDateTime(subscription.startDate).split(',')[0]],
            ['Expires',       subscription.endDate ? formatDateTime(subscription.endDate).split(',')[0] : 'No expiry'],
            ['Sold By',       subscription.purchasedBy?.name || '—'],
          ].map(([k, v]) => (
            <div key={k} className="om-info-row">
              <span className="om-info-label">{k}</span>
              <span className="om-info-val">{v}</span>
            </div>
          ))}
        </div>

        {/* Balance */}
        <div className="om-detail-section">
          <div className="om-section-title">Balance</div>
          <div className="sm-benefits-card">
            {subscription.planType === 'credit' ? (
              <>
                <div className="sm-benefit-row">
                  <span className="sm-benefit-icon">🪙</span>
                  <div>
                    <div className="sm-benefit-label">Credit Remaining</div>
                    <div className="sm-benefit-value">
                      {formatCurrency(subscription.creditRemaining ?? 0)}
                      <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400, marginLeft: 6 }}>
                        of {formatCurrency(subscription.creditIssued ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="sm-benefit-row">
                  <span className="sm-benefit-icon">📉</span>
                  <div>
                    <div className="sm-benefit-label">Credit Used</div>
                    <div className="sm-benefit-value">
                      {formatCurrency((subscription.creditIssued ?? 0) - (subscription.creditRemaining ?? 0))}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="sm-benefit-row">
                  <span className="sm-benefit-icon">👕</span>
                  <div>
                    <div className="sm-benefit-label">Pieces Remaining</div>
                    <div className="sm-benefit-value">
                      {subscription.piecesRemaining ?? 0}
                      <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 400, marginLeft: 6 }}>
                        of {subscription.piecesIssued ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="sm-benefit-row">
                  <span className="sm-benefit-icon">📉</span>
                  <div>
                    <div className="sm-benefit-label">Pieces Used</div>
                    <div className="sm-benefit-value">
                      {(subscription.piecesIssued ?? 0) - (subscription.piecesRemaining ?? 0)}
                    </div>
                  </div>
                </div>
              </>
            )}
            {usagePct != null && (
              <div className="ps-credit-bar-wrap" style={{ marginTop: 8 }}>
                <div
                  className="ps-credit-bar-fill"
                  style={{
                    width: `${100 - usagePct}%`,
                    background: usagePct < 25 ? '#ef4444' : usagePct < 60 ? '#f59e0b' : '#10b981',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {subscription.notes && (
        <div className="om-notes-box">
          <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Notes</span>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#374151' }}>{subscription.notes}</p>
        </div>
      )}

      {subscription.status === 'cancelled' && subscription.cancellationReason && (
        <div style={{
          background: '#fee2e2', border: '1.5px solid #fca5a5',
          borderRadius: 10, padding: '12px 16px',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', marginBottom: 4 }}>
            Cancellation Reason
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#7f1d1d' }}>{subscription.cancellationReason}</p>
          {subscription.cancelledAt && (
            <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>
              Cancelled on {formatDateTime(subscription.cancelledAt)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
